import React, { createContext, useCallback, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { GetColor } from '../../../style/color';
import type { DropMenuItem, MenuContainerProps } from '../types';
import NoItem from './NoItem';
import { Virtuoso } from 'react-virtuoso';
import { FS } from 'venn-utils';

export interface BaseMenuProps<TValue = string, TItem = DropMenuItem<TValue>> extends MenuContainerProps {
  items: TItem[];
  renderItem: (item: TItem, index: number) => JSX.Element;
  itemsContainerCss?: ReturnType<typeof css>;
  headerComponent?: JSX.Element;
  footerComponent?: JSX.Element;
  emptyStateComponent?: JSX.Element;
  menuTrailingContent?: JSX.Element;
  /** Temporary flag enabling the new dropdown styles */
  newStyle?: boolean;
}

type ListRenderProps<TValue = string, TItem = DropMenuItem<TValue>> = Pick<
  BaseMenuProps<TValue, TItem>,
  'height' | 'itemsContainerCss' | 'newStyle' | 'items' | 'menuTrailingContent' | 'renderItem'
>;

const isTestEnvironment = process.env.NODE_ENV === 'test';

const BaseMenu = <TValue, TItem = DropMenuItem<TValue>>({
  items,
  width,
  height = 175,
  renderItem,
  headerComponent,
  footerComponent,
  menuTrailingContent,
  emptyStateComponent,
  className,
  itemsContainerCss,
  newStyle,
}: BaseMenuProps<TValue, TItem>) => {
  const hasVirtualizationFF = FS.has('virtualization_ff');
  const shouldVirtualize = hasVirtualizationFF && !isTestEnvironment && items.length > 20;
  const listProps: ListRenderProps<TValue, TItem> = {
    height,
    itemsContainerCss,
    newStyle,
    menuTrailingContent,
    renderItem,
    items,
  };

  return (
    <Container width={width} height={height} tabIndex={-1} className={className} newStyle={newStyle}>
      {headerComponent}
      {!items || items.length === 0 ? (
        (emptyStateComponent ?? <NoItem>(No results found)</NoItem>)
      ) : shouldVirtualize ? (
        <VirtualisedList {...listProps} />
      ) : (
        <List {...listProps} />
      )}
      {footerComponent}
    </Container>
  );
};

const List = <TValue, TItem = DropMenuItem<TValue>>({
  itemsContainerCss,
  items,
  newStyle,
  menuTrailingContent,
  renderItem,
}: ListRenderProps<TItem, TValue>) => {
  return (
    <Items itemsContainerCss={itemsContainerCss} newStyle={newStyle}>
      {items.map(renderItem)}
      {menuTrailingContent}
    </Items>
  );
};

type ListContextValue = Pick<ListRenderProps, 'itemsContainerCss' | 'newStyle'>;

const ListContext = createContext<ListContextValue>({ itemsContainerCss: undefined, newStyle: false });
ListContext.displayName = 'ListContext';

const VirtualisedListContainer = React.forwardRef<HTMLDivElement>(function ListContainer(props, ref) {
  const { itemsContainerCss, newStyle } = useContext(ListContext);
  return <Items {...props} itemsContainerCss={itemsContainerCss} newStyle={newStyle} ref={ref} />;
});

const VirtualisedList = <TValue, TItem = DropMenuItem<TValue>>({
  itemsContainerCss,
  items,
  height,
  newStyle,
  menuTrailingContent,
  renderItem,
}: ListRenderProps<TItem, TValue>) => {
  const itemContent = useCallback((idx: number, item: TValue) => renderItem(item, idx), [renderItem]);

  const components = useMemo(() => {
    return {
      Footer: () => menuTrailingContent,
      List: VirtualisedListContainer,
    };
  }, [menuTrailingContent]);

  const ListContextValue: ListContextValue = useMemo(
    () => ({
      itemsContainerCss,
      newStyle,
    }),
    [itemsContainerCss, newStyle],
  );

  return (
    <ListContext.Provider value={ListContextValue}>
      <Virtuoso
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
        data={items}
        itemContent={itemContent}
        components={components}
      />
    </ListContext.Provider>
  );
};

export const Container = styled.div<MenuContainerProps & { newStyle?: boolean }>`
  display: flex;
  flex-direction: column;
  ${(props) => (props.width ? `width: ${props.width}px` : 'min-width: 200px; max-width: 100%;')};
  /* The spec says the max height should be 169px, but it's too small to fit 3 items without scroll. so 175 it is. */
  max-height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height)};
  background-color: ${GetColor.White};
  box-shadow: 0 5px 10px 0 ${GetColor.GreyScale.Grey60};

  ${({ newStyle }) =>
    newStyle &&
    css`
      box-shadow: 0 4px 4px 0 ${GetColor.GreyScale.Grey60};
      border: 1px solid ${GetColor.GreyScale.Grey30};
    `}
`;

export const Items = styled.div<ListContextValue>`
  overflow-y: auto;
  ${({ newStyle }) =>
    newStyle
      ? css`
          > *:not(:last-child) {
            border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
          }
        `
      : css`
          padding-top: 8px;
          padding-bottom: 8px;
        `}

  ${(props) => props.itemsContainerCss}
`;

export default BaseMenu;
